/**
 * Grenzschicht zwischen Appwrite-Zeilen und der Domäne.
 *
 * In der Datenbank liegen Zeitpunkte als ISO-Strings (lesbar in der Konsole,
 * sortierbar in Queries) und Pausen als JSON. In der Domäne sind Zeitpunkte
 * Zahlen und Pausen Objekte. Die Umwandlung passiert ausschliesslich hier.
 */

import type { Break, DayEntry, DayStatus, Employee } from './types.ts';

type Row = Record<string, unknown>;

const toTs = (v: unknown): number | null =>
  v === null || v === undefined || v === '' ? null : new Date(v as string).getTime();

const toIso = (v: number | null): string | null => (v === null ? null : new Date(v).toISOString());

function parseBreaks(raw: unknown): Break[] {
  if (!raw) return [];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((b) => b && typeof b.s === 'string')
      .map((b) => ({ s: new Date(b.s).getTime(), e: b.e ? new Date(b.e).getTime() : null }));
  } catch {
    return [];
  }
}

const serializeBreaks = (breaks: Break[]): string =>
  JSON.stringify(breaks.map((b) => ({ s: new Date(b.s).toISOString(), e: toIso(b.e) })));

export function parseDayEntry(row: Row): DayEntry {
  return {
    $id: row.$id as string | undefined,
    employeeId: String(row.employeeId ?? ''),
    date: String(row.date ?? ''),
    clockIn: toTs(row.clockIn),
    clockOut: toTs(row.clockOut),
    breaks: parseBreaks(row.breaks),
    netMinutes: row.netMinutes === null || row.netMinutes === undefined ? null : Number(row.netMinutes),
    status: (row.status as DayStatus) ?? 'open',
    corrected: Boolean(row.corrected),
    note: String(row.note ?? ''),
  };
}

/** Nur die schreibbaren Spalten — `$id` und Appwrite-Metadaten bleiben aussen vor. */
export function serializeDayEntry(entry: DayEntry): Row {
  return {
    employeeId: entry.employeeId,
    date: entry.date,
    clockIn: toIso(entry.clockIn),
    clockOut: toIso(entry.clockOut),
    breaks: serializeBreaks(entry.breaks),
    netMinutes: entry.netMinutes,
    status: entry.status,
    corrected: entry.corrected,
    note: entry.note,
  };
}

export function parseEmployee(row: Row): Employee {
  return {
    $id: row.$id as string | undefined,
    userId: String(row.userId ?? ''),
    name: String(row.name ?? ''),
    department: String(row.department ?? ''),
    weeklyTargetHours: Number(row.weeklyTargetHours ?? 40),
    workdaysPerWeek: Number(row.workdaysPerWeek ?? 5),
    openingBalanceHours: Number(row.openingBalanceHours ?? 0),
    employmentStart: String(row.employmentStart ?? ''),
    active: row.active !== false,
  };
}

/** Tage nach Datum indizieren — die Form, die alle Auswertungen brauchen. */
export function byDate(entries: DayEntry[]): Map<string, DayEntry> {
  return new Map(entries.map((e) => [e.date, e]));
}
